import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import i18n from '../i18next';
import { getAllUrlParams } from './CommonUtils';
import { FRONT_END_URL } from './Constants';
import {
    placeOrder,
    errorProcessingPayment,
    clearStripeHostedPaymentData,
    clearRyftData,
} from '../actions';

import { refreshStatusBarAfterInAppBrowser } from './initStatusBar';

export const STRIPE_RETURN_URL_SCHEME = 'menuu';

function getStripeReturnAppPath(appId) {
    return appId ? `/${encodeURIComponent(appId)}` : '';
}

export function buildStripeCheckoutSuccessUrl(appId) {
    return `${STRIPE_RETURN_URL_SCHEME}://payment-success${getStripeReturnAppPath(appId)}?success=true&session_id={CHECKOUT_SESSION_ID}`;
}

export function buildStripeCheckoutCancelUrl(appId) {
    return `${STRIPE_RETURN_URL_SCHEME}://payment-cancel${getStripeReturnAppPath(appId)}?success=false`;
}

/** Android GPay: Stripe redirects here; user taps through to menuu:// */
export function buildStripeCheckoutHppRedirectSuccessUrl(appId) {
    const appIdParam = appId ? `&app_id=${encodeURIComponent(appId)}` : '';
    return `${FRONT_END_URL}/payment-redirect.html?success=true&session_id={CHECKOUT_SESSION_ID}${appIdParam}`;
}

export function buildStripeCheckoutHppRedirectCancelUrl(appId) {
    const appIdParam = appId ? `&app_id=${encodeURIComponent(appId)}` : '';
    return `${FRONT_END_URL}/payment-redirect.html?success=false${appIdParam}`;
}

export function isStripeBrowserReturnUrl(url) {
    return (
        typeof url === 'string' &&
        (
            url.indexOf(`${STRIPE_RETURN_URL_SCHEME}://payment-success`) === 0 ||
            url.indexOf(`${STRIPE_RETURN_URL_SCHEME}://payment-cancel`) === 0
        )
    );
}

let storeRef = null;
let listenersAttached = false;
let successHandoffDone = false;
let stripeResumeDismissTimer = null;

export function resetStripeBrowserHandoffFlag() {
    successHandoffDone = false;
}

async function isStripeBrowserReturnUrlForThisApp(url) {
    if (isStripeBrowserReturnUrl(url)) {
        try {
            const appInfo = await App.getInfo();
            const parsedUrl = new URL(url);
            const appPath = appInfo && appInfo.id ? `/${appInfo.id}` : '';
            return !appPath || parsedUrl.pathname === appPath || parsedUrl.pathname === '';
        } catch (e) {
            return true;
        }
    }

    try {
        const parsedUrl = new URL(url);
        const appInfo = await App.getInfo();
        const appPath = appInfo && appInfo.id ? `/${appInfo.id}` : '';
        const isStripeReturnHost =
            parsedUrl.protocol === `${STRIPE_RETURN_URL_SCHEME}:` &&
            (parsedUrl.host === 'payment-success' || parsedUrl.host === 'payment-cancel');

        if (!isStripeReturnHost) {
            return false;
        }

        if (!appPath) {
            return true;
        }

        return parsedUrl.pathname === appPath || parsedUrl.pathname === '';
    } catch (e) {
        return true;
    }
}

/**
 * User left hosted checkout without a Stripe custom-scheme success/cancel URL (e.g. Android system back closed
 * Chrome Custom Tabs opened via OpenInChrome — those do not fire {@code Browser.browserFinished}).
 */
export function dismissStripeHostedCheckoutIfPending(store) {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
        return false;
    }
    if (!store) {
        return false;
    }
    if (successHandoffDone) {
        return false;
    }
    const s = store.getState();
    const ryftData = s?.paymentProvider?.ryftData;
    const stripeData = s?.paymentProvider?.stripeHostedPaymentData;
    if (!ryftData && !stripeData) {
        return false;
    }
    successHandoffDone = true;
    const providerId =
        (s.paymentProvider.providerType && s.paymentProvider.providerType.id) || 1;
    store.dispatch(
        errorProcessingPayment(
            {},
            i18n.t('screens:shoppingCartActions.cancelledByUser'),
            providerId
        )
    );
    store.dispatch(ryftData ? clearRyftData() : clearStripeHostedPaymentData());
    return true;
}

export async function handleStripeBrowserReturnUrl(store, url) {
    if (!url || !(await isStripeBrowserReturnUrlForThisApp(url))) {
        return;
    }

    try {
        await Browser.close();
    } catch (e) {
        /* Android may no-op close; ignore */
    }
    void refreshStatusBarAfterInAppBrowser();

    const params = getAllUrlParams(url);
    const sessionFromUrl = params.session_id;
    const state = store.getState();
    // The same menuu:// return is shared by Stripe and Ryft; only one checkout is ever in flight, so
    // discriminate by which provider stashed its hosted session. Ryft passes an OBJECT to placeOrder
    // ({paymentSessionId, amount, manualOrderId}); Stripe passes a STRING sessionId.
    const ryftData = state.paymentProvider.ryftData;
    const hostedSessionInState = state.paymentProvider.stripeHostedPaymentData;
    const stripeSessionId = sessionFromUrl || hostedSessionInState;
    const isRyft = !!ryftData;
    const providerId =
        (state.paymentProvider.providerType &&
            state.paymentProvider.providerType.id) ||
        1;

    const isSuccessPath = url.indexOf('payment-success') !== -1;
    const isCancelPath = url.indexOf('payment-cancel') !== -1;

    if (isSuccessPath) {
        if (successHandoffDone) {
            return;
        }
        successHandoffDone = true;

        if (params.success === 'true' && isRyft) {
            store.dispatch(placeOrder(false, ryftData));
            store.dispatch(clearRyftData());
        } else if (params.success === 'true' && stripeSessionId) {
            store.dispatch(placeOrder(false, stripeSessionId));
            store.dispatch(clearStripeHostedPaymentData());
        } else if (params.success === 'false') {
            store.dispatch(
                errorProcessingPayment({}, 'Something went wrong.', providerId)
            );
            store.dispatch(isRyft ? clearRyftData() : clearStripeHostedPaymentData());
        } else {
            successHandoffDone = false;
        }
        return;
    }

    if (isCancelPath) {
        if (successHandoffDone) {
            return;
        }
        successHandoffDone = true;
        store.dispatch(
            errorProcessingPayment(
                {},
                i18n.t('screens:shoppingCartActions.cancelledByUser'),
                providerId
            )
        );
        store.dispatch(isRyft ? clearRyftData() : clearStripeHostedPaymentData());
    }
}

async function processLaunchUrlIfStripe(store) {
    try {
        const { url } = await App.getLaunchUrl();
        if (url) {
            await handleStripeBrowserReturnUrl(store, url);
        }
    } catch (e) {
        /* No launch URL — normal */
    }
}

export function setupStripeBrowserDeepLinks(store) {
    if (Capacitor.getPlatform() !== 'android') {
        return;
    }
    storeRef = store;

    if (!listenersAttached) {
        listenersAttached = true;

        App.addListener('appUrlOpen', (data) => {
            if (data && data.url) {
                void handleStripeBrowserReturnUrl(storeRef, data.url);
            }
        });

        Browser.addListener('browserFinished', () => {
            dismissStripeHostedCheckoutIfPending(storeRef);
        });

        App.addListener('resume', () => {
            if (stripeResumeDismissTimer != null) {
                clearTimeout(stripeResumeDismissTimer);
            }
            stripeResumeDismissTimer = setTimeout(() => {
                stripeResumeDismissTimer = null;
                dismissStripeHostedCheckoutIfPending(storeRef);
            }, 400);
        });

        void processLaunchUrlIfStripe(storeRef);
    }
}
